import { Injectable, inject } from '@angular/core';
import {
  catchError,
  exhaustMap,
  filter,
  first,
  from,
  map,
  mergeMap,
  of,
  OperatorFunction,
  Subject,
  tap,
} from 'rxjs';
import { createEffect, ofType } from '@ngrx/effects';
import { CodeStore } from './code.reducer';
import { BaseEffect } from '../base.effect';
import { CodeService } from '../../../../../admin/src/app/dashboard/set-ups/parameters/code.service';

/**
 * Creates a resettable distinct operator that can be cleared when reset$ emits
 */
function resettableDistinct<T, K>(
  keySelector: (value: T) => K,
  reset$: Subject<void>,
): OperatorFunction<T, T> {
  const seen = new Set<K>();

  // Clear the seen set when reset$ emits
  // Note: This subscription will persist for the lifetime of the effect instance
  reset$.subscribe(() => seen.clear());

  return (source) =>
    source.pipe(
      filter((value) => {
        const key = keySelector(value);
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      }),
    );
}

@Injectable()
export class CodeEffect extends BaseEffect {
  protected service = inject(CodeService);

  // Subject to trigger reset of distinct checksums
  private readonly resetDistinct$ = new Subject<void>();

  searchCodes = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.searchCodes),
      // tap((action) => {
      //   console.log('searchCodes', action.query);
      //   debugger;
      // }),
      this.uS.ngRXChecksumGenerator('query'),
      // map(this.uS.ngRXChecksumGenerator('query')),
      resettableDistinct((action) => action.checksum, this.resetDistinct$),
      mergeMap(({ action }) => {
        // debugger;
        if (action.query.codeSubGroup) this.service.searchedSubGroups.add(action.query.codeSubGroup);
        return this.service.search(action.query).pipe(
          map((r) =>
            CodeStore.actions.searchCodesSuccess({
              result: r.content.map((x) => ({ ...x, active: 1 })),
            }),
          ),
          catchError((error) => of(CodeStore.actions.searchCodesError({ error }))),
        );
      }),
    ),
  );

  fetchAll = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.fetchAll),
      exhaustMap((action) =>
        this.service.search({}).pipe(
          map((r) =>
            CodeStore.actions.searchCodesSuccess({
              result: r.content.map((x) => ({ ...x, active: 1 })),
            }),
          ),
          catchError((error) => of(CodeStore.actions.searchCodesError({ error }))),
        ),
      ),
    ),
  );

  fetchAllSubgroups = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.fetchAllSubgroups),
      exhaustMap((action) =>
        this.service.fetchSubgroups().pipe(
          map((r) => CodeStore.actions.fetchAllSubgroupsSuccess({ result: r })),
          catchError((error) => of(CodeStore.actions.fetchAllSubgroupsError({ error }))),
        ),
      ),
    ),
  );

  createError = this.actions$.pipe(ofType(CodeStore.actions.createError));
  updateSuccess = this.actions$.pipe(
    ofType(CodeStore.actions.updateSuccess),
    tap(() => this.resetDistinct$.next()),
  );
  updateError = this.actions$.pipe(ofType(CodeStore.actions.updateError));

  create = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.create),
      exhaustMap((action) =>
        this.service.create(action.items).pipe(
          map((r) =>
            CodeStore.actions.createSuccess({
              result: r,
            }),
          ),
          catchError((error) => of(CodeStore.actions.createError({ error }))),
        ),
      ),
    ),
  );

  createSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.createSuccess),
      tap(() => this.resetDistinct$.next()),
      map((r) => CodeStore.actions.removeAllSubgroups()),
    ),
  );

  update = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.update),
      exhaustMap((action) =>
        this.service.update(action.items).pipe(
          map((r) =>
            CodeStore.actions.updateSuccess({
              result: r,
            }),
          ),
          catchError((error) => of(CodeStore.actions.updateError({ error }))),
        ),
      ),
    ),
  );

  delete = createEffect(() =>
    this.actions$.pipe(
      ofType(CodeStore.actions.delete),
      exhaustMap((action) =>
        this.service.deleteByID(action.id).pipe(
          map((r) =>
            CodeStore.actions.deleteSuccess({
              result: action.id,
            }),
          ),
          catchError((error) => of(CodeStore.actions.deleteError({ error }))),
        ),
      ),
    ),
  );

  deleteSuccess = this.actions$.pipe(
    ofType(CodeStore.actions.deleteSuccess),
    tap(() => this.resetDistinct$.next()),
  );
}
